import { Capacitor } from '@capacitor/core';
import { AdvertisingInfoResponse, Idfa } from '@sparkfabrik/capacitor-plugin-idfa';
import i18n from 'i18next';
import BrowserDetector from 'i18next-browser-languagedetector';
import HttpApi, { BackendOptions } from 'i18next-http-backend';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import React, { Component } from 'react';
import { initReactI18next, WithTranslation, withTranslation } from 'react-i18next';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import AdminPage from './admin/AdminPage';
import DecisionTreeEditorPage from './editor/DecisionTreeEditorPage';
import FormLoading from './form/components/FormLoading';
import FormPage from './form/FormPage';
import { FormService } from './form/services/FormService';
import MainLayout from './layout/MainLayout';
import AnonymousPage from './login/AnonymousPage';
import AdminRoute from './login/components/AdminRoute';
import PrivateRoute from './login/components/PrivateRoute';
import UserRoute from './login/components/UserRoute';
import LoginPage from './login/LoginPage';
import { initializeAnalytics } from './shared/services/AnalyticsService';
import UserPage from './user/UserPage'

async function fetchDefaultLanguage(): Promise<string> {
  try {
    const defaultLanguage = await fetch(`${process.env.REACT_APP_API_URL}/translations/defaultLanguage`);
    return defaultLanguage.text()
  } catch (error) {
    console.error("Error fetching default language:", error);
    return 'fr-be';
  }
}

async function getLastLanguageAvailability(defaultLanguage: string) {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/translations/ExistingLanguages`
    );

    if (!response.ok) {
      throw new Error(`Error fetching languages: ${response.statusText}`);
    }

    const availableLanguages = await response.json();
    const lastLanguage = localStorage.getItem('i18nextLng');

    if (lastLanguage) {
      for (let i = 0; i < availableLanguages.length; i++) {
        if (availableLanguages[i].includes(lastLanguage)) {
          return lastLanguage;
        }
      }
    }
    return defaultLanguage;
  } catch (error) {
    console.error('Error :', error);
    return defaultLanguage;
  }
}

export async function initializeI18n() {
  let defaultLanguage = await fetchDefaultLanguage();
  let lastLanguage = await getLastLanguageAvailability(defaultLanguage);
  const defaultValue = "fr-be"

  if ( !( lastLanguage.includes("-") && defaultLanguage.includes("-") ) ) {
    defaultLanguage = defaultValue
    lastLanguage = defaultValue
  }

  await i18n
    .use(initReactI18next)
    .use(BrowserDetector)
    .use(HttpApi)
    .init({
      lng: lastLanguage,
      fallbackLng: defaultLanguage,
      backend: {
        loadPath: `${process.env.REACT_APP_API_URL}/translations/{{lng}}`,
      } as BackendOptions,
    });

  return i18n;
}

interface State {
  isI18nInitialized: boolean;
  showCookieModal: boolean;
}
interface Props extends WithTranslation {
}

class App extends Component<Props, State> {
  private formService: FormService;
  private isMobile = Capacitor.isNativePlatform();

  constructor(props: Props) {
    super(props);
    this.formService = new FormService();

    this.state = {
      isI18nInitialized: false,
      showCookieModal: false,
    };
  }

  async componentDidMount() {
    const consentCookie = document.cookie
      .split("; ")
      .find(row => row.startsWith("CookieConsent="));

    if (consentCookie === null || consentCookie === undefined) {
      this.setState({ showCookieModal: true });
    }
    else {
      const consentGiven = consentCookie && consentCookie.split("=")[1] === "true";
      consentGiven ? this.enableGoogleAnalytics() : this.disableGoogleAnalytics();
    }

    try {
      await initializeI18n();
      this.setState({ isI18nInitialized: true })
      this.updateTitle();

      if (!this.isMobile && this.props.i18n.language) {
        this.initLanguage();
      }

      if (typeof this.props.i18n.on === 'function') {
        if (!this.isMobile) {
          this.props.i18n.on("languageChanged", this.updateLanguage.bind(this));
        }
      }
    }
    catch (error) {
      console.error(error);
    }
    }

  componentWillUnmount() {
    if (typeof this.props.i18n.on === 'function') {
      this.props.i18n.off("languageChanged", this.updateTitle.bind(this));
      if (!this.isMobile) {
        this.props.i18n.off("languageChanged", this.updateLanguage.bind(this));
      }
   }
  }

  initLanguage() {
    const url = new URL(window.location.href);
    let locale = url.searchParams.get('locale');

    if (locale) {

      if (!locale?.includes("-") ) {
        locale = locale.includes("nl") || locale.includes("fr")
          ? locale + "-be"
          : locale = locale + "-eu"
      }

      if (locale !== this.props.i18n.language) {
        this.props.i18n.changeLanguage(locale);
      }
    }
  }

  updateLanguage(language: string) {
    if (language.includes("-")) {
      language = language.split('-')[0];
    }

    const url = new URL(window.location.href);
    url.searchParams.set('locale', language);
    window.history.pushState({}, '', url.toString());
  }

  updateTitle() {
    document.title = this.formService.t(this.props.t, 'title_app', false, true);
    const favicon = document.getElementById("favicon");
    if (favicon) {
      (favicon as HTMLLinkElement).href = this.formService.t(this.props.t, 'favicon', false, false, false, true);
    }
  }

  disableGoogleAnalytics() {
    document.cookie = "_ga=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = "_gid=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = "_ga_Y84NSF7J09=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    console.log("Google Analytics deactivated");
  }

  enableGoogleAnalytics() {
    if (process.env["REACT_APP_GA_ID"]) {
      Idfa.getAdvertisingInfo()
        .then((response: AdvertisingInfoResponse) => {
          if (Capacitor.getPlatform() !== "web" && response.isAdTrackingLimited) {
            console.log("ReactGA will not be initialized because ad tracking is limited.");
          } else {
            initializeAnalytics(process.env["REACT_APP_GA_ID"] as string);
            console.log("Google Analytics activated");
          }
        })
        .catch((err: Error) => {
          console.error("Error fetching Idfa advertising info:", err);
          // Fallback: Initialize GA even if Idfa fails, as it might still be valid on web
          if (Capacitor.getPlatform() === "web") {
            initializeAnalytics(process.env["REACT_APP_GA_ID"] as string);

            console.log("Google Analytics activated");
          }
        });
    } else {
      console.warn("No REACT_APP_GA_ID provided, Google Analytics not initialized.");
    }
  }

  //region COOKIE CONSENT

  getCookie(name) {
    return document.cookie
      .split('; ')
      .find(row => row.startsWith(name + '='))?.split('=')[1] ?? null;
  };

  setCookie(name, value, days = 365) {
    const maxAge = days * 24 * 60 * 60;
    document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
  };

  handleAcceptCookies = () => {
    this.setCookie('CookieConsent', 'true', 365);
    this.enableGoogleAnalytics();
    this.setState({ showCookieModal: false });
  };

  handleDeclineCookies = () => {
    this.setCookie('CookieConsent', 'false', 365);
    this.disableGoogleAnalytics();
    this.setState({ showCookieModal: false });
  };

  renderCookieFooter = () => (
    <div>
      <Button
        label={this.formService.t(this.props.t, 'cookie_consent_button_decline', true, true)}
        style={{ marginRight: 20, border: "none", backgroundColor: "#ec6607" }}
        className="p-button-rounded "
        onClick={this.handleDeclineCookies}
      />
      <Button
        label={this.formService.t(this.props.t, 'cookie_consent_button_confirm', true, true)}
        style={{ marginRight: 20, border: "none", backgroundColor: "#ec6607" }}
        className="p-button-rounded "
        onClick={this.handleAcceptCookies}
        autoFocus
      />
    </div>
  );

//endregion

  render() {
    if (!this.state.isI18nInitialized) {
      return <FormLoading/>
    }

    return (
      <React.Fragment>
        <Router>
          <Switch>
            <Route path="/home">
              <AnonymousPage/>
            </Route>
            <Route path="/login">
              <LoginPage/>
            </Route>
            <UserRoute path="/user">
              <MainLayout>
                <UserPage/>
              </MainLayout>
            </UserRoute>
            <AdminRoute path="/admin">
              <AdminPage/>
            </AdminRoute>
            <AdminRoute path="/editor">
              <DecisionTreeEditorPage/>
            </AdminRoute>
            <PrivateRoute path="/">
              <MainLayout>
                <FormPage/>
              </MainLayout>
            </PrivateRoute>
          </Switch>
        </Router>
        {Capacitor.isNativePlatform() ? '' :
          <Dialog
            visible={this.state.showCookieModal}
            modal
            style={{ width: '40rem', maxWidth: '95vw' }}
            footer={this.renderCookieFooter()}
            closable={false}
            dismissableMask={false}
            onHide={() => {}}
            contentClassName="cookie-consent-content"
          >
            <p className="cookie-consent-text">
              {this.formService.t(this.props.t, 'cookie_consent_text', true, true)}
            </p>
          </Dialog>
        }
      </React.Fragment>
    );
  }
}

export default withTranslation()(App);
